import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useTheme } from '@ow/theme'
import StyledPopup from './styled/popup'
import { StyledContainer, StyledContent, StyledFooter } from './styled/content'
import Header from './Header'
import RenderIf from '../render-if'

const sizeMap = { sm: '480px', lg: '960px', md: '960px' }

const defaultWidth = (isFullScreen, size, width) => {
  if (isFullScreen) {
    return '100vw'
  }
  if (size) {
    return sizeMap[size]
  }
  return width
}

const Modal = forwardRef((props, ref) => {
  const { themeClass } = useTheme()

  const {
    className,
    trigger,
    open,
    on,
    children,
    position,
    offsetX,
    offsetY,
    closeOnDocumentClick,
    closeOnEscape,
    repositionOnResize,
    contentStyle,
    overlayStyle,
    nested,
    size,
    width,
    isFullScreen,
    contentPadding,
    maxHeight,
    title,
    footer,
    isFooterComp,
    showCloseButton,
    onOpen,
    onClose,
    otherFooterData,
  } = props

  return (
    <StyledPopup
      ref={ref}
      trigger={trigger}
      open={open}
      on={on}
      position={position}
      offsetX={offsetX}
      offsetY={offsetY}
      modal
      closeOnDocumentClick={closeOnDocumentClick}
      closeOnEscape={closeOnEscape}
      repositionOnResize={repositionOnResize}
      overlayStyle={overlayStyle}
      nested={nested}
      showCloseButton={showCloseButton}
      lockScroll
      onOpen={onOpen}
      onClose={onClose}
      contentStyle={{
        width: defaultWidth(isFullScreen, size, width),
        borderRadius: '5px',
        height: isFullScreen ? '100vh' : '',
        boxShadow:
          '0px 0px 1px 0px rgb(82 100 132 / 20%), 0px 8px 15.52px 0.48px rgb(28 43 70 / 15%)',
        padding: '0',
        borderWidth: '0',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        backgroundClip: 'padding-box',
        border: '0 solid rgba(0,0,0,0)',
        outline: 0,
        position: 'relative',
        ...contentStyle,
      }}
    >
      {(close) => (
        <StyledContainer
          className={clsx(
            themeClass,
            'dark:bg-darkTheme-1100 dark:text-white',
            className
          )}
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <RenderIf isTrue={title}>
            <Header
              title={title}
              showCloseButton={showCloseButton}
              close={close}
            />
          </RenderIf>
          <StyledContent
            contentPadding={contentPadding}
            maxHeight={maxHeight}
            isFullScreen={isFullScreen}
          >
            {children}
          </StyledContent>
          <RenderIf isTrue={footer}>
            <StyledFooter className="dark:border-t dark:border-darkTheme-800 dark:border-solid">
              {footer && (
                <div className="footer-box">
                  {isFooterComp ? footer : footer({ close, otherFooterData })}
                </div>
              )}
            </StyledFooter>
          </RenderIf>
        </StyledContainer>
      )}
    </StyledPopup>
  )
})

Modal.propTypes = {
  className: PropTypes.string,
  trigger: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  on: PropTypes.arrayOf(PropTypes.string),
  width: PropTypes.string,
  contentPadding: PropTypes.string,
  maxHeight: PropTypes.string,
  children: PropTypes.node,
  position: PropTypes.arrayOf(PropTypes.string),
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  open: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  closeOnDocumentClick: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  repositionOnResize: PropTypes.bool,
  contentStyle: PropTypes.shape({}),
  overlayStyle: PropTypes.shape({}),
  nested: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  footer: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  size: PropTypes.string,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
}
Modal.defaultProps = {
  className: undefined,
  trigger: undefined,
  open: false,
  on: undefined,
  children: undefined,
  position: undefined,
  offsetX: undefined,
  offsetY: undefined,
  isFullScreen: undefined,
  closeOnDocumentClick: true,
  closeOnEscape: true,
  repositionOnResize: true,
  contentStyle: undefined,
  overlayStyle: undefined,
  nested: false,
  size: undefined,
  width: undefined,
  contentPadding: undefined,
  maxHeight: undefined,
  title: undefined,
  footer: undefined,
  showCloseButton: true,
  onOpen: undefined,
  onClose: undefined,
}

Modal.displayName = 'Modal'

export default Modal
